// Newsletter subscription helper — client-side caller for the site's
// Beehiiv proxy endpoint at /api/beehiiv-subscribe.
//
// Architecture (since May 2026):
//   browser → POST /api/beehiiv-subscribe (Netlify Function, holds API key)
//          → Beehiiv v2 API (Bearer auth, server-to-server)
//          → parallel fire to Make Realtime Hub for Telegram alert
//
// Why we no longer call Beehiiv from the browser:
//   - The API key MUST stay server-side (otherwise anyone in DevTools
//     gets it). VITE_BEEHIIV_PUBLICATION_ID public was OK, but the
//     key was a security hole.
//   - CORS preflights to api.beehiiv.com added latency.
//   - Server-side gives us a single audit trail in Netlify logs.

import { trackEvent } from './analytics.js';

const SUBSCRIBE_ENDPOINT = '/api/beehiiv-subscribe';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
  return typeof email === 'string' && EMAIL_RE.test(email.trim());
}

/**
 * Subscribe an email to the newsletter via the server-side proxy.
 *
 * @param {{ email: string, source?: string, leadMagnet?: string, lang?: string }} payload
 * @returns {Promise<{ status: 'success' | 'already' | 'error', message?: string }>}
 */
export async function subscribeToNewsletter({ email, source = 'site', leadMagnet, lang = 'fr', persona, stage }) {
  if (!isValidEmail(email)) {
    return { status: 'error', message: 'invalid_email' };
  }

  // Dev convenience: in `npm run dev` the Netlify Function isn't running.
  // Simulate a success so the UX flow can be tested.
  if (import.meta.env.DEV) {
    await new Promise((r) => setTimeout(r, 600));
    return { status: 'success' };
  }

  try {
    const res = await fetch(SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        source,
        leadMagnet,
        lang,
        // T8 — journey tags forwarded to Beehiiv custom_fields (server-side).
        persona,
        stage,
      }),
    });

    const data = await res.json().catch(() => ({}));
    // Handler always returns a normalized { status } shape.
    if (data && (data.status === 'success' || data.status === 'already' || data.status === 'error')) {
      // Fire GA4 newsletter_signup event on success — feeds the funnel
      // R1 (Visit → Subscribe) ratio in /admin GA4 Funnel section.
      // `already` also counts: a returning subscriber re-engages = signal.
      if (data.status === 'success' || data.status === 'already') {
        trackEvent('newsletter_signup', {
          source,
          lead_magnet: leadMagnet || 'none',
          lang,
          status: data.status,
        });
      }
      return data;
    }

    if (res.ok) {
      trackEvent('newsletter_signup', { source, lead_magnet: leadMagnet || 'none', lang, status: 'success' });
      return { status: 'success' };
    }
    return { status: 'error', message: `http_${res.status}` };
  } catch (err) {
    return { status: 'error', message: err?.message ?? 'network' };
  }
}
